import { Injectable } from '@angular/core';
import { RegistrationCoreConfig } from '../registration/registration-core-config.interface';

/** Default clientId used when none is resolved. */
const DEFAULT_CLIENT_ID = 'ttpoi-web';

/** Source value sent for landing-page submissions. */
const LANDING_SOURCE = 'partner-campaign';

/**
 * Holds the resolved landing configuration for a registration core instance.
 * Provides the resolved `source`, payload extension building, and landing state.
 *
 * Not providedIn root — provided per component instance or in embed providers.
 */
@Injectable()
export class LandingConfigService {
  /** Whether this instance is operating in landing mode. */
  isLanding: boolean = false;

  /** Resolved theme name. */
  theme: string | undefined = undefined;

  /** Resolved client ID. */
  clientId: string | undefined = undefined;

  /**
   * Resolves the outgoing `source` value.
   * - Landing page: always `partner-campaign`.
   * - Otherwise: the resolved clientId (defaulting to `ttpoi-web`).
   */
  resolveSource(): string {
    if (this.isLanding) {
      return LANDING_SOURCE;
    }
    return this.clientId || DEFAULT_CLIENT_ID;
  }

  /**
   * Builds the payload extension object for the registration submission.
   * Always includes `is_landing` and a deterministic `source`.
   *
   * `source` is `partner-campaign` for the landing page, otherwise the clientId.
   */
  buildPayloadExtension(): { is_landing: boolean; source: string } {
    return {
      is_landing: this.isLanding,
      source: this.resolveSource(),
    };
  }

  /**
   * Sets all properties from the given configuration object.
   */
  configure(config: RegistrationCoreConfig): void {
    this.isLanding = config.isLanding ?? false;
    this.theme = config.theme;
    this.clientId = config.clientId;
  }
}
